import { useCallback, useEffect, useMemo } from 'react';

import emptyPng from '@assets/empty.png';
import { PageTitle } from '@components/PageTitle';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from '@components/ui/form';
import { Input } from '@components/ui/input';
import { ENoteTypeFile } from '@enums/NoteTypeFile';
import { zodResolver } from '@hookform/resolvers/zod';
import { type Note } from '@models/Note';
import { type IDeleteFolderInputData } from '@services/folders/dtos/deleteFolder/InputData';
import { type IUpdateFolderInputData } from '@services/folders/dtos/updateFolder/InputData';
import FolderService from '@services/folders/FolderService';
import { type IDeleteNoteInputData } from '@services/notes/dtos/deleteNote/InputData';
import { type IGetAllNotesOutputData } from '@services/notes/dtos/getAllNotes/OutputData';
import NoteService from '@services/notes/NoteService';
import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { useParams, useSearchParams } from 'react-router';
import { z } from 'zod';

import HeaderButton from '../HeaderButton';
import { type IOpenMoveFolderInputData } from '../MoveDialog';
import File from './Item/File';
import Folder from './Item/Folder';

interface IProps {
  idUpdateFolder?: string;
  isPendingUpdateFolder: boolean;
  onOpenMoveDialog: (inputData: IOpenMoveFolderInputData) => void;
  onOpenRenameDialog: (note: Note) => void;
  onUpdateFolder: (data: IUpdateFolderInputData) => Promise<void>;
}

const formSchema = z.object({
  name: z.string(),
});

function NotesList({
  idUpdateFolder,
  isPendingUpdateFolder,
  onOpenMoveDialog,
  onOpenRenameDialog,
  onUpdateFolder,
}: IProps) {
  const params = useParams();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    mode: 'onBlur',
    defaultValues: {
      name: searchParams.get('name') || '',
    },
  });

  const { data } = useSuspenseQuery<IGetAllNotesOutputData>({
    queryFn: async () => {
      try {
        const response = await NoteService.getAll({
          folderId: params.id ? params.id : undefined,
        });
        return response;
      } catch (error) {
        console.log(error);
      }
      return {
        list: [],
        path: [],
      };
    },
    queryKey: ['notes', params.id],
    refetchOnWindowFocus: false,
  });

  const {
    isPending: isPendingDeleteFolder,
    mutateAsync: mutateAsyncDeleteFolder,
    variables: variablesDeleteFolder,
  } = useMutation<void, unknown, IDeleteFolderInputData>({
    mutationFn: FolderService.delete,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notes'] }),
  });

  const {
    isPending: isPendingDeleteNote,
    mutateAsync: mutateAsyncDeleteNote,
    variables: variablesDeleteNote,
  } = useMutation<void, unknown, IDeleteNoteInputData>({
    mutationFn: NoteService.delete,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notes'] }),
  });

  const handleDeleteFolder = useCallback(
    async (data: IDeleteFolderInputData) => {
      await mutateAsyncDeleteFolder(data);
    },
    [mutateAsyncDeleteFolder],
  );

  const handleDeleteNote = useCallback(
    async (data: IDeleteNoteInputData) => {
      await mutateAsyncDeleteNote(data);
    },
    [mutateAsyncDeleteNote],
  );

  function onSubmit(): void {
    //
  }

  const listFiltered = useMemo(() => {
    if (searchParams.get('name')) {
      return data.list.filter((item) =>
        item.title
          .toLowerCase()
          .includes(searchParams.get('name')!.toLowerCase()),
      );
    }
    return data.list;
  }, [data.list, searchParams]);

  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (name === 'name') {
        const valuesFiltered = Object.fromEntries(
          Object.entries(value).filter(([_, value]) => value !== ''),
        );
        setSearchParams(
          () => ({
            ...valuesFiltered,
          }),
          { replace: true },
        );
      }
    });
    return () => subscription.unsubscribe();
  }, [form, setSearchParams]);

  return (
    <div>
      <div className="flex flex-col gap-x-2">
        <PageTitle hasBackButton title="Anotações" />
        <HeaderButton />
      </div>
      <Form {...form}>
        <form
          className="flex flex-col justify-end gap-2 sm:flex-row"
          onSubmit={form.handleSubmit(onSubmit)}>
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem className="flex w-full flex-col sm:max-w-72">
                <FormLabel className="text-tertiary">Nome</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
              </FormItem>
            )}
          />
        </form>
      </Form>
      {listFiltered?.length ? (
        <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {listFiltered
            .filter((item) => item.category === 'PASTA')
            .map((item) => (
              <Folder
                {...item}
                isLoading={
                  (isPendingUpdateFolder && idUpdateFolder === item.id) ||
                  (isPendingDeleteFolder &&
                    variablesDeleteFolder.id === item.id)
                }
                key={item.id}
                onDelete={handleDeleteFolder}
                onOpenMoveDialog={onOpenMoveDialog}
                onOpenRenameDialog={onOpenRenameDialog}
                onUpdate={onUpdateFolder}
              />
            ))}

          {listFiltered
            .filter((item) => item.category === ENoteTypeFile.Annotation)
            .map((item) => (
              <File
                {...item}
                isLoading={
                  isPendingDeleteNote && variablesDeleteNote.id === item.id
                }
                key={item.id}
                onDelete={handleDeleteNote}
                onOpenMoveDialog={onOpenMoveDialog}
              />
            ))}
        </div>
      ) : (
        <div className="mt-36 flex flex-col justify-center">
          <img
            alt="Imagem de listagem vazia"
            className="mx-auto max-w-80"
            src={emptyPng}
          />
          <p className="mt-4 text-center text-lg font-bold">
            Não há registros!
          </p>
          <p className="mt-4 text-center text-lg">
            Não encontramos anotações suas em nosso sistema
          </p>
        </div>
      )}
    </div>
  );
}

export default NotesList;
